import React, {useState} from "react";
import {Link} from "react-router";
import {
  Button,
  CircularProgress,
  IconButton,
  Switch,
  TextField,
} from "@material-ui/core";
import LinkIcon from "@material-ui/icons/Link";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import ArchiveIcon from "@material-ui/icons/Archive";
import ArchivedChip from "common_components/project/archived_project_chip";
import {IssuesetId, PlaybookIssueset} from "common/types/issueset";
import {Issue, IssueId} from "common/types/issue";
import {OrganisationId} from "common/types/organisation";
import {Project} from "common/types/project";
import {Id} from "common/types";
import {ContractType} from "./contract_types_list";
import {Workflow, WorkflowId} from "common/flowmaster/types/workflow";
import WorkflowSelector from "common_components/issue_editor/components/workflow_selector";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";

interface IssuesPanelProps {
  selectedContractType?: ContractType;
  selectedIssueset?: PlaybookIssueset;
  selectedIssue: Issue | null;
  groupedIssues: Array<{name: string; item: Issue[]}>;
  issueState: Record<string, boolean>;
  organisationId: OrganisationId;
  projects: Project[];
  onSelectIssue: (selectedIssue: Issue) => () => void;
  onToggleIssue: (issueId: IssueId) => void;
  onArchiveIssueset: (
    contractTypeId: Id,
    issuesetId: IssuesetId,
    isArchived: boolean,
  ) => void;
  onHideIssueset: (
    contractTypeId: Id,
    issuesetId: IssuesetId,
    isHidden: boolean,
  ) => void;
  isAdmin: boolean;
  workflows: Workflow[];
  onUpdateDefaultWorkflow: (
    contractTypeId: number,
    issuesetId: IssuesetId,
    defaultWorkflowId: WorkflowId,
  ) => void;
  canSetIssuesetDefaultWorkflow: boolean;
  onUpdatePreparationWorkflow: (
    contractTypeId: number,
    issuesetId: IssuesetId,
    preparationWorkflowId: WorkflowId | null,
  ) => void;
  onAddIssue: (issueName: string) => void;
}

const styles: Record<string, React.CSSProperties> = {
  issueLink: {
    color: "#ccc",
    textDecoration: "none",
    cursor: "pointer",
  },
  issueContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "1rem",
    border: "1px solid #eee",
    alignItems: "baseline",
    cursor: "pointer",
  },
  addIssueContainer: {
    padding: ".5rem",
    margin: "0 1rem",
    display: "flex",
    gap: "1rem",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  textField: {
    backgroundColor: "#404a57",
    color: "white",
    borderRadius: "4px",
    flex: 1,
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    color: "white",
  },
  selectedIssue: {
    backgroundColor: "white",
    color: "black",
  },
  issueContent: {
    display: "flex",
    alignItems: "center",
  },
  header: {
    textAlign: "center",
    top: 0,
    backgroundColor: "white",
    fontWeight: "bold",
    color: "black",
    padding: "1em",
    zIndex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: 30,
  },
  mainContainer: {
    padding: "10px 0px",
    backgroundColor: "#232e38",
    color: "#fff",
    height: "calc(100vh - 125px - 30px)",
    overflow: "auto",
  },
  groupHeader: {
    fontWeight: "bold",
    padding: "1rem",
    margin: "0 1rem",
    border: "1px solid white",
  },
  workflowSelector: {
    width: "100%",
    maxWidth: "22em",
    boxSizing: "border-box",
    padding: ".25rem .5rem",
  },
  preparationSelector: {
    display: "flex",
    alignItems: "center",
    gap: ".5rem",
  },
  headerBottom: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 6px",
    marginBottom: "6px",
  },
};

const IssuesPanel: React.FC<IssuesPanelProps> = ({
  selectedContractType,
  selectedIssueset,
  selectedIssue,
  groupedIssues,
  issueState,
  organisationId,
  projects,
  onSelectIssue,
  onToggleIssue,
  onArchiveIssueset,
  onHideIssueset,
  isAdmin,
  workflows,
  onUpdateDefaultWorkflow,
  canSetIssuesetDefaultWorkflow,
  onUpdatePreparationWorkflow,
  onAddIssue,
}) => {
  const [isAddingIssue, setIsAddingIssue] = useState(false);
  const [newIssueName, setNewIssueName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleAddIssue = async () => {
    if (!newIssueName.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      await onAddIssue(newIssueName);
      setNewIssueName("");
      setIsAddingIssue(false);
    } finally {
      setIsLoading(false);
    }
  };

  const renderAddIssue = () => {
    if (isLoading) {
      return (
        <div style={styles.addIssueContainer}>
          <div style={styles.loadingContainer}>
            <CircularProgress size={20} color="inherit" />
            <span>Creating issue...</span>
          </div>
        </div>
      );
    }

    if (isAddingIssue) {
      return (
        <div style={styles.addIssueContainer}>
          <TextField
            style={styles.textField}
            value={newIssueName}
            onChange={e => setNewIssueName(e.target.value)}
            placeholder="Enter issue name"
            variant="outlined"
            size="small"
            autoFocus
            InputProps={{
              style: {color: "white"},
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            style={{
              color: "white",
              fontWeight: "bold",
            }}
            onClick={handleAddIssue}
            disabled={!newIssueName.trim()}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            style={{color: "white"}}
            onClick={() => {
              setIsAddingIssue(false);
              setNewIssueName("");
            }}
          >
            Cancel
          </Button>
        </div>
      );
    }

    return (
      <div style={styles.addIssueContainer}>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          style={{color: "white"}}
          onClick={() => setIsAddingIssue(true)}
        >
          Add Issue
        </Button>
      </div>
    );
  };

  const renderIssueLink = (issue: Issue) => {
    if (!selectedIssueset || !selectedContractType) {
      return null;
    }

    const issuesetId = selectedIssueset.id;
    const contractTypeProjects = selectedContractType.projects ?? [];
    const projectId =
      contractTypeProjects.find(projectId =>
        (projects || [])
          .find(_project => _project?.id === projectId)
          ?.issuesets?.find(projIssuesetId => issuesetId === projIssuesetId),
      ) || contractTypeProjects[0];

    const path = `/organisation/${organisationId}/issue/${issue.id}`;
    const query = `?open_issue_list=true&issueset=${issuesetId}&project=${projectId}&auto_select_first_document=true`;

    return (
      <Link to={`${path}${query}`} style={styles.issueLink}>
        <LinkIcon />
      </Link>
    );
  };

  const renderIssue = (issue: Issue) => {
    const isSelected = selectedIssue && issue.id === selectedIssue.id;
    const isOn = issueState[issue.id] !== false;

    return (
      <div
        key={issue.id}
        style={{
          ...styles.issueContainer,
          ...(isSelected ? styles.selectedIssue : {}),
        }}
        onClick={onSelectIssue(issue)}
      >
        <div style={styles.issueContent}>
          {renderIssueLink(issue)}
          <span style={{marginLeft: ".7em"}}>{issue.name}</span>
          {issue.is_archived && <ArchivedChip />}
        </div>
        {isAdmin && (
          <Switch
            checked={isOn}
            color="primary"
            onChange={() => onToggleIssue(issue.id)}
          />
        )}
      </div>
    );
  };

  if (!selectedContractType || !selectedIssueset) {
    return null;
  }

  return (
    <div style={{width: "40em"}}>
      <div style={styles.header}>
        <div />
        {selectedContractType.name} - {selectedIssueset.name}
        <div style={{display: "flex", gap: "8px"}}>
          {isAdmin && (
            <div
              title={
                selectedIssueset.is_hidden ? "Show Issueset" : "Hide Issueset"
              }
              style={{cursor: "pointer"}}
              onClick={() =>
                onHideIssueset(
                  selectedContractType.id,
                  selectedIssueset.id,
                  selectedIssueset.is_hidden,
                )
              }
            >
              {selectedIssueset.is_hidden ? (
                <VisibilityOffIcon style={{color: "#aaa"}} />
              ) : (
                <VisibilityIcon style={{color: "#424242"}} />
              )}
            </div>
          )}
          <div
            title={`${
              selectedIssueset.is_archived ? "Unarchive" : "Archive"
            } Issueset`}
          >
            <ArchiveIcon
              style={{
                cursor: "pointer",
                color: !selectedIssueset.is_archived ? "#424242" : "#aaa",
              }}
              onClick={() =>
                onArchiveIssueset(
                  selectedContractType.id,
                  selectedIssueset.id,
                  selectedIssueset.is_archived,
                )
              }
            />
          </div>
        </div>
      </div>
      {(isAdmin || canSetIssuesetDefaultWorkflow) && (
        <div style={styles.headerBottom}>
          <div style={styles.workflowSelector}>
            <WorkflowSelector
              label="Issueset default workflow"
              fullWidth
              organisationId={organisationId}
              workflows={workflows}
              selectedWorkflowId={selectedIssueset.default_workflow_id}
              updateselectedWorkflowId={(workflowId: WorkflowId) =>
                onUpdateDefaultWorkflow(
                  selectedContractType.id,
                  selectedIssueset.id,
                  workflowId,
                )
              }
              showStarredWorkflows
            />
          </div>
          {isAdmin && (
            <div
              style={{
                ...styles.workflowSelector,
                ...styles.preparationSelector,
              }}
            >
              <WorkflowSelector
                label="Document preparation workflow"
                fullWidth
                organisationId={organisationId}
                workflows={workflows}
                selectedWorkflowId={
                  selectedIssueset.preparation_workflow_id ?? ("" as never)
                }
                updateselectedWorkflowId={(workflowId: WorkflowId) =>
                  onUpdatePreparationWorkflow(
                    selectedContractType.id,
                    selectedIssueset.id,
                    workflowId,
                  )
                }
                showStarredWorkflows
              />
              {Boolean(selectedIssueset.preparation_workflow_id) && (
                <IconButton
                  size="small"
                  title="Clear"
                  onClick={() =>
                    onUpdatePreparationWorkflow(
                      selectedContractType.id,
                      selectedIssueset.id,
                      null,
                    )
                  }
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          )}
        </div>
      )}
      <div style={styles.mainContainer}>
        {Boolean(selectedIssueset.default_workflow_id) && renderAddIssue()}
        {groupedIssues.map((issueGroup, index) => (
          <div key={`${issueGroup.name}-${index}`}>
            <div style={styles.groupHeader}>{issueGroup.name}</div>
            <div
              style={{
                backgroundColor: "#353f48",
                margin: "0 1rem",
              }}
            >
              {issueGroup.item.map(issue => renderIssue(issue))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IssuesPanel;
